import { useEffect, useMemo, useState, useRef } from "react";
import BasicPanel from "../../../components/panels/basicPanel/basicPanel";
import InputVoiceSubmit from "../../../components/inputVoice/inputVoiceSubmit";
import ShareButton from "../../../components/shareButton/ShareButton";

import { useInvoiceContext } from "../../createInvoice/createInvoiceContext";
import arrangeInvoiceParams from "../../createInvoice/steps/utils/arrangeInvoiceParams";
import { useMessenger } from "../../../context/messengerContext";
import { buildUserIdPayload } from "../../../utils/userIdPayload";
import { useTranslation } from "react-i18next";
import { calculateInvoice } from "../../../api/logistic";
import { useLanguage } from "../../../context/localeContext";
import { Modal } from "rsuite";
import Btn from "../../../components/Btn/Btn";

import "./calculateResult.css";
import { getCurrencySymbol } from "../../../utils/currencyHelper";

const getDeliveryTypesFrom = (t) => ({
    1: t("door"),
    2: t("door"),
    3: t("warehouse"),
    4: t("warehouse"),
  })
  
  const getDeliveryTypesTo = (t) => ({
    1: t("door"),
    2: t("warehouse"),
    3: t("door"),
    4: t("warehouse"),
  })

const LabelWithSubLabel = ({ label, subLabel }) => {
    return (
        <div className={`labelWithSubLabel`}>
            <div className="paragraph-14R" style={{color: "var(--Gray-800)"}}>{label}</div>
            <div className="paragraph-12R" style={{color: "var(--Gray-600) !important"}}>{subLabel}</div>
        </div>
    )
  }
  
  const ValueWithSubValue = ({ value, subValue }) => {
    return (
        <div className={`valueWithSubValue`}>
            <div className="paragraph-14R" style={{color: "var(--Gray-900)"}}>{value}</div>
            <div className="paragraph-10R" style={{color: "var(--Gray-600)"}}>{subValue}</div>
        </div>
    )
  }
  
  const LabelWithValueAndKey = ({ label, subLabel, value, subValue }) => {
    return (
        <div className={`labelWithValueAndKey`}>
            <LabelWithSubLabel label={label} subLabel={subLabel} />
            <ValueWithSubValue value={value} subValue={subValue} />
        </div>
    )
  }

export default function CalculateResult({startTime}) {
    const { invoiceData } = useInvoiceContext();
    const { userId, platform, goToMenu } = useMessenger();
    const { t } = useTranslation('basic');
    const [calculatedWeight, setCalculatedWeight] = useState(0);
    const { language } = useLanguage();
    const currencySymbol = getCurrencySymbol(invoiceData?.user_info);

    const [success, setSuccess] = useState(false);
    const [calcResult, setCalcResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const shareCalcRef = useRef(null);

    console.log('invoiceData', invoiceData)

    useEffect(() => {
        const calculated_weight = invoiceData.places.reduce((sum, place) => sum + place.weight, 0);
        setCalculatedWeight(calculated_weight);
    }, [invoiceData]);

    const shareData = useMemo(() => {
        // Берём данные из ref-снапшота, чтобы не зависеть от возможных изменений invoiceData
        const snapshot = shareCalcRef.current;
        if (!snapshot) return null;
        return {
            title: "Расчёт стоимости доставки",
            text: [
                `Маршрут: ${snapshot.cityFromLabel} → ${snapshot.cityToLabel}`,
                `Тариф: ${snapshot.tariff_name}`,
                `Срок: ${snapshot.period_min}–${snapshot.period_max} раб. дней`,
                `Стоимость: ${snapshot.delivery_sum ?? snapshot.delivery_pay_amount ?? ""} ${snapshot.currencySymbol}`,
            ].join('\n')
        };
    }, [
        shareCalcRef.current,
    ]);

    const extraFields = useMemo(() => {
        const r = calcResult;
        if (!r || typeof r !== "object") return [];
        const hidden = new Set([
            "delivery_sum",
            "delivery_pay_amount",
            "tariff_name",
            "period_min",
            "period_max",
        ]);
        return Object.entries(r)
            .filter(([k, v]) => !hidden.has(k) && (typeof v === "string" || typeof v === "number" || typeof v === "boolean"))
            .map(([k, v]) => ({ key: k, value: String(v) }));
    }, [calcResult]);

    return (
        <div style={{
            height: "calc(100vh - 10rem)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between"
        }}>
        <BasicPanel header={t('calculation_parameters')}>
            <div className="inputRows" style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem"
            }}>
                <LabelWithValueAndKey label={t('total_size')}
                    value={invoiceData.places[0].width + ' x ' + invoiceData.places[0].length + ' x ' + invoiceData.places[0].height + ' см'} />
                
                <LabelWithValueAndKey label={t('total_weight')}
                    value={calculatedWeight + ' кг'} />
                
                <LabelWithValueAndKey label={t('calculate_weight')}
                    value={invoiceData.places
      .reduce((acc, place) => acc + (place.height * place.width * place.length) / 5000, 0)
      .toFixed(2) + ' кг'} />
                
                <LabelWithValueAndKey label={t('cityFrom')}
                    value={invoiceData.cityFromLabel} />
                
                <LabelWithValueAndKey label={t('cityTo')}
                    value={invoiceData.cityToLabel} />

                <LabelWithValueAndKey label={t('deliveryType')}
                    value={getDeliveryTypesFrom(t)[invoiceData.selected_mode] + '-' + getDeliveryTypesTo(t)[invoiceData.selected_mode]} />
                
                <LabelWithValueAndKey label={t('delivery_sum')}
                    value={invoiceData.delivery_sum + ' ' + currencySymbol} />

                <LabelWithValueAndKey label={t('deliveryPeriod')}
                    value={invoiceData.period_min && invoiceData.period_max 
                        ? `от ${invoiceData.period_min} до ${invoiceData.period_max} раб. дней`
                        : t('not_specified')} />
            </div>
        </BasicPanel>
        {success ? (
            <div className="calcSuccessBlock">
                <div className="paragraph-14M" style={{ color: "var(--Gray-900)" }}>
                    {t("delivery_sum")}
                </div>

                <div className="calcSuccessRows">
                    {calcResult?.delivery_sum != null && (
                        <LabelWithValueAndKey
                            label={t('delivery_sum')}
                            value={`${calcResult.delivery_sum} ${currencySymbol}`}
                        />
                    )}

                    {calcResult?.tariff_name && (
                        <LabelWithValueAndKey
                            label={t('deliveryMode')}
                            value={calcResult.tariff_name}
                        />
                    )}

                    {(calcResult?.period_min != null || calcResult?.period_max != null) && (
                        <LabelWithValueAndKey
                            label={t('deliveryPeriod')}
                            value={
                                calcResult?.period_min && calcResult?.period_max
                                    ? `от ${calcResult.period_min} до ${calcResult.period_max} раб. дней`
                                    : t('not_specified')
                            }
                        />
                    )}

                    {extraFields.map((f) => (
                        <LabelWithValueAndKey
                            key={f.key}
                            label={t(f.key)}
                            value={f.value}
                        />
                    ))}
                </div>
            </div>
        ) : (
        <InputVoiceSubmit loading={loading} onNavigateForward={async () => {
            if (loading) return;
            const dateStartSec = Math.floor(startTime.getTime() / 1000);
            const dateEndSec = Math.floor(Date.now() / 1000);
            const session = {
                contract_number: invoiceData.contract_number,
                total_time: dateEndSec - dateStartSec,
                date_start: dateStartSec,
                date_end: dateEndSec
            };

            let d = arrangeInvoiceParams(invoiceData);

            try {
                setLoading(true);
                let combinedServies = {}

                // Map service key to object with count if type is not bool and price from invoiceData.additions_separated_prices
                Object.entries(invoiceData.additions_separated_prices).forEach(([key, value]) => {
                    combinedServies[key] = {
                        count: invoiceData.additions[key],
                        price: value
                    }
                })

                const response = await calculateInvoice({ platform, userId }, {...d, services: combinedServies, ...buildUserIdPayload(platform, userId),language: language || "ru"}, session);
                if (response.result) {
                    // Сохраняем снапшот данных для шаринга
                    shareCalcRef.current = {
                        cityFromLabel: invoiceData.cityFromLabel,
                        cityToLabel: invoiceData.cityToLabel,
                        tariff_name: invoiceData.tariff_name,
                        period_min: invoiceData.period_min,
                        period_max: invoiceData.period_max,
                        delivery_sum: response.result?.delivery_sum,
                        delivery_pay_amount: response.result?.delivery_pay_amount,
                        currencySymbol,
                    };

                    setCalcResult(response.result);
                    setSuccess(true);
                    setShowSuccessModal(true);
                    setLoading(false);
                } else {
                    setLoading(false);
                    console.error("Ошибка при расчете счета:", response.error);
                }
            } catch (error) {
                setLoading(false);
                console.error("Ошибка выполнения запроса:", error);
            }
          
            }} />
        )}

        {success && (
            <Modal
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                size="xs"
            >
                <Modal.Header>
                    <Modal.Title>{t("delivery_sum")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Основная информация уже показана выше в calcSuccessBlock */}
                </Modal.Body>
                <Modal.Footer>
                    <div className='confirmModal_footer'>
                        <ShareButton className="primary" data={shareData}>
                            {t("share_button")}
                        </ShareButton>
                        <Btn
                            text={t("go_to_menu")}
                            fluid
                            onClick={goToMenu}
                        />
                    </div>
                </Modal.Footer>
            </Modal>
        )}

        </div>
    )
}